"use client";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import {
  CrossSellViewMode,
  type ITheme,
} from "@/api/AppApi/services/company/interfaces/ICompany";
import type { IProduct } from "@/api/AppApi/services/home/interfaces/IProduct";
import { useGetCrossSellProductsQuery } from "@/api/AppApi/services/product";
import { useTenantText } from "@/hooks/localization/useTenantText";

export interface CartCrossSell {
  /** Cross-sell is switched on and this surface is the configured one. */
  enabled: boolean;
  title: string;
  asGrid: boolean;
  size: Size;
  theme?: ITheme;
  products: IProduct[];
  /** Nothing has arrived yet — show a skeleton, not an empty state. */
  isLoading: boolean;
  hasProducts: boolean;
}

/**
 * Cart cross-sell recommendations for one surface.
 *
 * One request, a plain array back: `/products/cross-sell` resolves the basket
 * server-side from the `Agent-Id` header, merges in any "Show for All" list, and
 * deliberately bypasses catalog visibility so a hidden product or a product in a
 * hidden category can still be recommended. None of that is the storefront's
 * business any more — render what comes back.
 *
 * @param ids     every product id in the cart, comma separated — see
 *                `useCartCrossSellIds`. Empty means there is nothing to ask about.
 * @param surface which caller this is, so the hook only fetches for the surface
 *                the merchant configured — otherwise the cart page would request
 *                recommendations it never renders in popup mode.
 * @param active  false parks the query (e.g. once the popup is dismissed).
 */
export const useCartCrossSell = (
  ids: string,
  surface: CrossSellViewMode,
  active = true,
): CartCrossSell => {
  const tenantText = useTenantText();
  const { data: companyData } = useGetCompanyQuery();
  const theme = companyData?.result?.theme;
  const cartSettings = theme?.components?.cart;

  const viewMode =
    cartSettings?.crosssell_view_mode === CrossSellViewMode.popup
      ? CrossSellViewMode.popup
      : CrossSellViewMode.section;
  const enabled = Boolean(
    active &&
      ids &&
      theme &&
      cartSettings?.show_crosssell_products &&
      surface === viewMode,
  );

  const query = useGetCrossSellProductsQuery({ params: { ids }, enabled });
  const products = query.data?.result ?? [];

  return {
    enabled,
    // Merchant copy: may arrive as a plain string or an {en,ar} JSON map.
    title: tenantText(cartSettings?.crosssell_products_title),
    asGrid: Boolean(cartSettings?.crosssell_products_asgrid),
    size: theme?.components?.product?.product_box_style ?? Size.small,
    theme,
    products,
    isLoading: enabled && query.isInitialLoading,
    hasProducts: products.length > 0,
  };
};
